import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  Eye, 
  RefreshCw,
  Search,
  Filter,
  Calendar,
  Mail,
  Edit,
  UserPlus,
  Link2,
  XCircle,
  Wrench,
} from 'lucide-react';
import { ProjectData } from '../types';
import { auth } from '../lib/firebase';
import { ProjectDetails } from './ProjectDetails';
import { InviteClientDialog } from './InviteClientDialog';
import { LinkJiraDialog } from './LinkJiraDialog';

const JIRA_BROWSE_BASE = 'https://teamideal.atlassian.net/browse';

interface AdminDashboardProps {
  onViewProject: (jiraKey: string) => void;
}

export function AdminDashboard({ onViewProject }: AdminDashboardProps) {
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCancelled, setShowCancelled] = useState(false);
  const [appTypeFilter, setAppTypeFilter] = useState<string>('all');
  const [customerUpdateModalProject, setCustomerUpdateModalProject] = useState<string | null>(null);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false); // IS-1431
  const [linkJiraProject, setLinkJiraProject] = useState<ProjectData | null>(null);
  const [syncingKey, setSyncingKey] = useState<string | null>(null);
  const [cancellingKey, setCancellingKey] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const isPendingProject = (project: ProjectData) =>
    project.pendingJiraCreation === true || project.jiraKey.startsWith('PENDING-');

  // Fetch projects from backend
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error('Admin session expired — sign in again to load projects');
      }

      const response = await fetch(
        'https://us-central1-ima-onboard.cloudfunctions.net/getAllProjects',
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      
      if (!response.ok) {
        const detail = await response.json().catch(() => null);
        throw new Error(detail?.error || 'Failed to fetch projects');
      }
      
      const data = await response.json();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
      setActionMessage(
        error instanceof Error ? error.message : 'Failed to load projects'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleSyncFromJira = async (jiraKey: string) => {
    setActionMessage(null);
    setSyncingKey(jiraKey);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error('Admin session expired — sign in again to sync');
      }
      const response = await fetch(
        'https://us-central1-ima-onboard.cloudfunctions.net/syncProjectFromJira',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ jiraKey }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Sync failed');
      }
      const parts: string[] = [];
      if (data.data?.statusUpdated) parts.push('status');
      if (data.data?.formFieldsUpdated > 0) parts.push(`${data.data.formFieldsUpdated} fields`);
      if (data.data?.customerUpdated) parts.push('customer info');
      setActionMessage(
        parts.length > 0
          ? `Synced ${jiraKey}: updated ${parts.join(', ')}`
          : `Synced ${jiraKey}: already up to date`
      );
      await fetchProjects();
    } catch (error) {
      setActionMessage(
        error instanceof Error ? error.message : `Failed to sync ${jiraKey}`
      );
    } finally {
      setSyncingKey(null);
    }
  };

  const handleCancelProject = async (project: ProjectData) => {
    const projectKey = project.jiraKey;
    const confirmed = window.confirm(
      isPendingProject(project)
        ? `Cancel pending invite ${projectKey}?\n\nThe invite will be hidden from the admin list (status: cancelled). This does not delete Firestore data.`
        : `Cancel ${projectKey}?\n\nIt will be hidden from the admin list (status: cancelled). Firestore data is kept and the Jira issue is not changed, so a later Sync from Jira can bring it back.`
    );
    if (!confirmed) return;

    setActionMessage(null);
    setCancellingKey(projectKey);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error('Admin session expired — sign in again to cancel');
      }
      const response = await fetch(
        'https://us-central1-ima-onboard.cloudfunctions.net/cancelPendingProject',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ pendingKey: projectKey }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Cancel failed');
      }
      setActionMessage(`Cancelled ${projectKey}`);
      await fetchProjects();
    } catch (error) {
      setActionMessage(
        error instanceof Error ? error.message : `Failed to cancel ${projectKey}`
      );
    } finally {
      setCancellingKey(null);
    }
  };

  // Filter projects based on search and filters (IS-1715: hide cancelled by default)
  // IMA-3: haystack includes appName/companyDescription; multi-word tokens must all match
  const filteredProjects = projects.filter(project => {
    const normalize = (value: string | null | undefined) =>
      (value || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const haystack = [
      project.jiraKey,
      project.customerName,
      project.customerEmail,
      project.formData?.appName,
      project.formData?.companyDescription,
    ]
      .map(normalize)
      .filter(Boolean)
      .join(' ');

    const tokens = normalize(searchTerm).split(' ').filter(Boolean);
    const matchesSearch =
      tokens.length === 0 || tokens.every((token) => haystack.includes(token));

    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    const matchesAppType = appTypeFilter === 'all' || project.appType === appTypeFilter;
    const matchesCancelled = showCancelled || project.status !== 'cancelled';

    return matchesSearch && matchesStatus && matchesAppType && matchesCancelled;
  });

  // Get status display info
  const getStatusInfo = (status: string) => {
    const statusMap: { [key: string]: { label: string; color: string; icon: any } } = {
      'stage1_awaiting_customer': { label: 'Awaiting Customer', color: 'text-yellow-600 bg-yellow-100', icon: Clock },
      'stage2_provisioning': { label: 'Provisioning', color: 'text-blue-600 bg-blue-100', icon: RefreshCw },
      'stage3_complete': { label: 'Complete', color: 'text-green-600 bg-green-100', icon: CheckCircle },
      'stage3_development': { label: 'Building Config', color: 'text-blue-600 bg-blue-100', icon: RefreshCw },
      'stage4_deployed': { label: 'Deployed', color: 'text-purple-600 bg-purple-100', icon: CheckCircle },
      'stage4_app_store_registration': { label: 'App Store Registration', color: 'text-indigo-600 bg-indigo-100', icon: RefreshCw },
      'stage5_testing': { label: 'Testing', color: 'text-teal-600 bg-teal-100', icon: Clock },
      'stage6_internal_approval': { label: 'Internal Approval', color: 'text-slate-600 bg-slate-100', icon: CheckCircle },
      'customer_review': { label: 'Customer Review', color: 'text-cyan-600 bg-cyan-100', icon: Eye },
      'resolve_issues': { label: 'Resolve Issues', color: 'text-orange-600 bg-orange-100', icon: Wrench },
      'waiting_for_model_file': { label: 'Waiting for Model File', color: 'text-amber-600 bg-amber-100', icon: AlertTriangle },
      'inventory_clean_up_required': { label: 'Inventory Clean Up', color: 'text-amber-600 bg-amber-100', icon: AlertTriangle },
      'apple_agreement_update_needed': { label: 'Apple Agreement', color: 'text-orange-600 bg-orange-100', icon: AlertTriangle },
      'submit_to_app_store': { label: 'Submitting', color: 'text-indigo-600 bg-indigo-100', icon: RefreshCw },
      'waiting_on_app_store_approval': { label: 'App Store Review', color: 'text-amber-600 bg-amber-100', icon: Clock },
      'deployment_finished': { label: 'Finished', color: 'text-green-600 bg-green-100', icon: CheckCircle },
      'stage_for_update': { label: 'Update Ready', color: 'text-blue-600 bg-blue-100', icon: RefreshCw },
      'on_hold': { label: 'On Hold', color: 'text-gray-600 bg-gray-100', icon: Clock },
      'cancelled': { label: 'Cancelled', color: 'text-red-600 bg-red-100', icon: XCircle },
    };
    
    return statusMap[status] || { label: status, color: 'text-gray-600 bg-gray-100', icon: Clock };
  };

  // Get unique statuses and app types for filters
  const uniqueStatuses = [...new Set(projects.map(p => p.status))];
  const uniqueAppTypes = [...new Set(projects.map(p => p.appType))];

  // Calculate statistics (exclude cancelled from defaults)
  const activeProjects = projects.filter(p => p.status !== 'cancelled');
  const stats = {
    total: activeProjects.length,
    inProgress: activeProjects.filter(p =>
      [
        'stage1_awaiting_customer',
        'stage2_provisioning',
        'customer_review',
        'apple_agreement_update_needed',
        'waiting_for_model_file',
        'inventory_clean_up_required',
      ].includes(p.status)
    ).length,
    completed: activeProjects.filter(p => ['deployment_finished'].includes(p.status)).length,
    needsAttention: activeProjects.filter(p =>
      [
        'customer_review',
        'apple_agreement_update_needed',
        'waiting_for_model_file',
        'inventory_clean_up_required',
      ].includes(p.status)
    ).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Onboarding Dashboard</h1>
          <p className="text-gray-600">Monitor and manage customer onboarding projects</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setIsInviteDialogOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <UserPlus className="h-4 w-4" />
            Invite Client
          </button>
          <button
            onClick={fetchProjects}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Invite Client Dialog (IS-1431) */}
      <InviteClientDialog
        isOpen={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
        onSuccess={() => {
          fetchProjects(); // Refresh the project list
        }}
      />

      <LinkJiraDialog
        isOpen={!!linkJiraProject}
        pendingKey={linkJiraProject?.jiraKey || ''}
        customerName={linkJiraProject?.customerName || ''}
        appType={linkJiraProject?.appType || 'versirent'}
        onClose={() => setLinkJiraProject(null)}
        onSuccess={() => {
          setActionMessage(
            linkJiraProject
              ? `Linked ${linkJiraProject.jiraKey} to Jira`
              : 'Linked to Jira'
          );
          fetchProjects();
        }}
      />

      {actionMessage && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-center justify-between gap-3">
          <span>{actionMessage}</span>
          <button
            type="button"
            onClick={() => setActionMessage(null)}
            className="text-blue-600 hover:text-blue-900 text-xs font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Projects</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <RefreshCw className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <AlertTriangle className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Needs Attention</p>
              <p className="text-2xl font-bold text-gray-900">{stats.needsAttention}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Search className="h-4 w-4 inline mr-1" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by Jira key, customer name, or email..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="h-4 w-4 inline mr-1" />
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>
                  {getStatusInfo(status).label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="h-4 w-4 inline mr-1" />
              App Type
            </label>
            <select
              value={appTypeFilter}
              onChange={(e) => setAppTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All App Types</option>
              {uniqueAppTypes.map(appType => (
                <option key={appType} value={appType}>
                  {appType === 'versirent' ? 'VersiRent' : 'IdealOne'}
                </option>
              ))}
            </select>
          </div>
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={showCancelled}
            onChange={(e) => setShowCancelled(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Show cancelled
        </label>
      </div>

      {/* Projects Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Projects ({filteredProjects.length})
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProjects.map((project) => {
                const statusInfo = getStatusInfo(project.status);
                const StatusIcon = statusInfo.icon;
                
                return (
                  <React.Fragment key={project.jiraKey}>
                    <tr className="hover:bg-gray-50 border-l-4 border-l-blue-500">
                      <td className="px-4 py-4">
                        <div className="max-w-xs">
                          <div className="text-sm font-medium text-gray-900 flex items-center flex-wrap gap-1">
                            {isPendingProject(project) ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-amber-100 text-amber-800">
                                {project.jiraKey}
                              </span>
                            ) : (
                              <a
                                href={`${JIRA_BROWSE_BASE}/${project.jiraKey}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-blue-100 text-blue-800 hover:bg-blue-200 hover:underline"
                                title={`Open ${project.jiraKey} in Jira`}
                              >
                                {project.jiraKey}
                              </a>
                            )}
                            {isPendingProject(project) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                Awaiting Jira
                              </span>
                            )}
                            <span className="truncate">{project.formData.appName || 'Unnamed App'}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Click to expand ↓
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-4 py-4">
                        <div className="max-w-xs">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {project.customerName}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center">
                            <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{project.customerEmail}</span>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          project.appType === 'versirent' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {project.appType === 'versirent' ? 'VR' : 'IO'}
                        </span>
                      </td>
                      
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          <span className="hidden sm:inline">{statusInfo.label}</span>
                        </span>
                      </td>
                      
                      <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="hidden md:inline">{new Date(project.updatedAt).toLocaleDateString()}</span>
                          <span className="md:hidden">{new Date(project.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        </div>
                      </td>
                      
                      <td className="px-4 py-4 text-sm font-medium">
                        <div className="flex items-center gap-2">
                          {isPendingProject(project) ? (
                            <>
                              <button
                                onClick={() => setLinkJiraProject(project)}
                                className="flex items-center gap-1 text-amber-700 hover:text-amber-900 whitespace-nowrap"
                                title="Link an existing Jira issue"
                              >
                                <Link2 className="h-4 w-4" />
                                <span className="hidden lg:inline">Link Jira</span>
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => onViewProject(project.jiraKey)}
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-900 whitespace-nowrap"
                                title="View project (opens in new tab)"
                              >
                                <Eye className="h-4 w-4" />
                                <span className="hidden lg:inline">View</span>
                              </button>
                              <button
                                onClick={() => handleSyncFromJira(project.jiraKey)}
                                disabled={syncingKey === project.jiraKey}
                                className="flex items-center gap-1 text-indigo-600 hover:text-indigo-900 whitespace-nowrap disabled:opacity-50"
                                title="Pull status and fields from Jira"
                              >
                                <RefreshCw className={`h-4 w-4 ${syncingKey === project.jiraKey ? 'animate-spin' : ''}`} />
                                <span className="hidden lg:inline">
                                  {syncingKey === project.jiraKey ? 'Syncing…' : 'Sync'}
                                </span>
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setCustomerUpdateModalProject(project.jiraKey)}
                            className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
                            title="Update customer information"
                          >
                            <Edit className="h-4 w-4" />
                            <span className="hidden lg:inline">Edit</span>
                          </button>
                          {project.status !== 'cancelled' && (
                            <button
                              onClick={() => handleCancelProject(project)}
                              disabled={cancellingKey === project.jiraKey}
                              className="flex items-center gap-1 text-red-600 hover:text-red-800 whitespace-nowrap disabled:opacity-50"
                              title="Hide this project from the admin list (status: cancelled)"
                            >
                              <XCircle className="h-4 w-4" />
                              <span className="hidden lg:inline">
                                {cancellingKey === project.jiraKey ? 'Cancelling…' : 'Cancel'}
                              </span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={6} className="p-0">
                        <ProjectDetails 
                          project={project} 
                          triggerCustomerUpdateModal={customerUpdateModalProject === project.jiraKey}
                          onCustomerUpdateModalClose={() => setCustomerUpdateModalProject(null)}
                        />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No projects found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || appTypeFilter !== 'all' || showCancelled
                ? 'Try adjusting your search or filters.'
                : 'No onboarding projects have been created yet.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
